import React, { useCallback, useState } from "react";
import { Check, AlertCircle } from "lucide-react";
import Turnstile from "react-turnstile";
import { logIssueNetworkRequest } from "../../utils/Loggers";
import { displayToastMessage } from "../../utils/Toasts";
import SessionService from "../../services/Session";
import { errorsToLabels, logAndFormatFormError } from "../../utils/Errors";
import { Mixpanel } from "../../utils/Analytics";
import UserService from "../../services/User";
import config from "../../config";

const TurnstileComponent = React.memo(({ onChange }: any) => {
  return (
    <Turnstile
      className="w-full py-3 mt-2 mb-4"
      size="normal"
      sitekey={config.turnstile.siteKey}
      onVerify={onChange}
    />
  );
});

const AuthForm = ({
  emailToken = "",
  selectedPlan = "basic",
  initialView = "login",
  onAuthSuccess,
}: any) => {
  const [view, setView] = useState(initialView); // login, signup, forgotPassword, resetPassword
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activationToken, setActivationToken] = useState(emailToken);
  const [resetToken, setResetToken] = useState(emailToken);
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleLogIn = useCallback(async () => {
    try {
      logIssueNetworkRequest("handleLogIn");
      displayToastMessage("info", "Loading...");

      const sessionService = new SessionService();
      const resBody = await sessionService.logIn({
        email,
        password,
        selectedPlan,
      });
      if (resBody.error) {
        if (resBody.error === errorsToLabels.userMustActivateAccount) {
          displayToastMessage(
            "success",
            "Please check your email for an activation token"
          );
          setView("activateAccount");
          return;
        }
        throw new Error(resBody.error);
      }

      displayToastMessage("success", "Success");

      if (resBody.sessionURL) {
        // New subscription - redirect to Stripe Checkout
        window.location.href = resBody.sessionURL;
        return;
      }

      Mixpanel.identify(resBody.user.id);
      Mixpanel.track("Log In (start)", {
        email,
      });

      onAuthSuccess(resBody.user);
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "AuthForm.handleLogIn",
        error,
        "Logging in"
      );
      displayToastMessage("error", errorMessage);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, password, onAuthSuccess, selectedPlan]);

  const handleLogInWithGoogle = useCallback(async () => {
    try {
      logIssueNetworkRequest("handleLogInWithGoogle");
      displayToastMessage("info", "Loading...");

      const sessionService = new SessionService();
      const resBody = await sessionService.logInWithGoogle();
      if (resBody.error) {
        throw new Error(resBody.error);
      }

      displayToastMessage("success", "Success");

      window.location.href = resBody.googleAuthUrl;
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "AuthForm.handleLogInWithGoogle",
        error,
        "Logging in with Google"
      );
      displayToastMessage("error", errorMessage);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const handleSignUp = useCallback(async () => {
    try {
      logIssueNetworkRequest("handleSignUp");
      displayToastMessage("info", "Loading...");

      const userService = new UserService();
      const resBody = await userService.signUp({
        email,
        name,
        password,
      });
      if (resBody.error) {
        throw new Error(resBody.error);
      }
      if (resBody.accountLink) {
        window.open(resBody.accountLink.url, "_blank")?.focus();
      }

      Mixpanel.track("Sign Up", {
        email,
      });

      displayToastMessage("success", "Redirecting to checkout...");

      try {
        if (process.env.NODE_ENV === "production") {
          (window as any).gtag("event", "conversion", {
            send_to: "AW-649623932/k_WXCO6Km7QaEPzy4bUC",
          });
        }
      } catch (error: any) {
        console.log("gtag conversion error:", error);
      }

      // New subscription - redirect to Stripe Checkout
      window.location.href = resBody.sessionURL;
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "AuthForm.handleSignUp",
        error,
        "Signing up"
      );
      displayToastMessage("error", errorMessage);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, name, password]);

  const handleActivateAccount = useCallback(async () => {
    try {
      logIssueNetworkRequest("handleActivateAccount");
      displayToastMessage("info", "Loading...");

      Mixpanel.track("Activate Account", {
        email,
      });

      const userService = new UserService();
      const resBody = await userService.activateAccount({
        email,
        activationToken,
      });
      if (resBody.error) {
        throw new Error(resBody.error);
      }

      displayToastMessage("success", "Success! Reloading...");

      // TODO: Review bug on successful activations where logging in returns in a circular JSON structure in the request...
      window.location.href = config.links.baseWebURL;
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "AuthForm.handleActivateAccount",
        error,
        "Activating account"
      );
      displayToastMessage("error", errorMessage);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, activationToken]);

  const handleForgotPassword = useCallback(async () => {
    try {
      logIssueNetworkRequest("handleForgotPassword");
      displayToastMessage("info", "Loading...");

      const userService = new UserService();
      const resBody = await userService.forgotPassword({
        email,
      });
      if (resBody.error) {
        throw new Error(resBody.error);
      }

      displayToastMessage(
        "success",
        "Please check your email for your reset token"
      );
      setView("resetPassword");
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "AuthForm.handleForgotPassword",
        error,
        "Resetting password"
      );
      displayToastMessage("error", errorMessage);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [email]);

  const handleResetPassword = useCallback(async () => {
    try {
      logIssueNetworkRequest("handleResetPassword");
      displayToastMessage("info", "Loading...");

      const userService = new UserService();
      const resBody = await userService.resetPassword({
        email,
        resetToken,
        newPassword: password,
      });
      if (resBody.error) {
        throw new Error(resBody.error);
      }

      displayToastMessage("success", "Password reset successfully");
      setView("login");
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "AuthForm.handleResetPassword",
        error,
        "Resetting password"
      );
      displayToastMessage("error", errorMessage);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, password, resetToken]);

  const handleVerifyTurnstileSubmit = useCallback(async (token: any) => {
    try {
      logIssueNetworkRequest("handleVerifyTurnstileSubmit");
      console.log("handleVerifyTurnstileSubmit(): ", token);

      const sessionService = new SessionService();
      const resBody = await sessionService.verifyTurnstileToken({
        token,
      });
      if (resBody.error) {
        throw new Error(resBody.error);
      }

      console.log("Recaptcha success");
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "AuthForm.handleVerifyTurnstileSubmit",
        error,
        "Verifying Recaptcha token"
      );
      displayToastMessage("error", errorMessage);
      displayToastMessage("error", "Reload the page and try again.");
      // NOTE: blocks them from submitting until reload...
      setIsSubmitting(true);
    }
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Reset errors
    setErrors({});

    // Validate inputs
    let newErrors: any = {};

    if (!email) {
      newErrors.email = "Email is required.";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email.";
    }

    if (view === "login" || view === "signup") {
      if (!password) {
        newErrors.password = "Password is required.";
      } else if (password.length < 8) {
        newErrors.password = "Password must be at least 8 characters.";
      }
    }

    if (view === "signup" && !name) {
      newErrors.name = "Name is required.";
    }

    if (view === "activateAccount") {
      if (!activationToken) {
        newErrors.activationToken = "Activation token is required.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      switch (view) {
        case "login":
          await handleLogIn();
          break;
        case "signup":
          await handleSignUp();
          break;
        case "activateAccount":
          await handleActivateAccount();
          break;
        case "forgotPassword":
          await handleForgotPassword();
          break;
        case "resetPassword":
          await handleResetPassword();
          break;
        default:
          break;
      }
    } catch (error: Error | any) {
      setIsSubmitting(false);
      setErrors({
        general: error.message || "Something went wrong. Please try again.",
      });
    }
  };

  const renderView = () => {
    switch (view) {
      case "login":
        return (
          <>
            <h2 className="text-2xl font-semibold mb-6">
              Log in to GoViralStrategy
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="Enter your password"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.password}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setView("forgotPassword")}
                className="text-sm text-purple-600 hover:text-purple-800 transition-colors"
              >
                Forgot password?
              </button>

              <TurnstileComponent onChange={handleVerifyTurnstileSubmit} />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-70 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Logging in...
                  </>
                ) : (
                  "Log in"
                )}
              </button>
            </form>

            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <button
                  disabled={isSubmitting}
                  onClick={handleLogInWithGoogle}
                  className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm bg-white hover:bg-gray-50 transition-colors"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </button>
                <p className="text-sm text-gray-600 text-center">
                  <strong>Important:</strong> You must create an account before
                  logging in with Google. New users should complete the
                  registration process first.
                </p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setView("signup")}
                  className="text-purple-600 hover:text-purple-800 font-medium transition-colors"
                >
                  Sign up
                </button>
              </p>
            </div>
          </>
        );

      case "signup":
        return (
          <>
            <h2 className="text-2xl font-semibold mb-6">
              Sign up for GoViralStrategy
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="Your name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="Create a password (min. 8 characters)"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="text-sm text-gray-600">
                <p>
                  By signing up, you agree to our{" "}
                  <a
                    href="/legal/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>

              <TurnstileComponent onChange={handleVerifyTurnstileSubmit} />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-70 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating account...
                  </>
                ) : (
                  "Sign up"
                )}
              </button>
            </form>

            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <button
                  onClick={handleLogInWithGoogle}
                  className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm bg-white hover:bg-gray-50 transition-colors"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </button>
                <p className="text-sm text-gray-600 text-center">
                  <strong>Important:</strong> You must create an account before
                  logging in with Google. New users should complete the
                  registration process first.
                </p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setView("login")}
                  className="text-purple-600 hover:text-purple-800 font-medium transition-colors"
                >
                  Log in
                </button>
              </p>
            </div>
          </>
        );

      case "activateAccount":
        return (
          <>
            <h2 className="text-2xl font-semibold mb-6">
              Activate your account
            </h2>
            <p className="text-gray-600 mb-6">
              Enter your email and the activation token sent to your inbox to
              activate your account.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activation Token
                </label>
                <input
                  type="text"
                  value={activationToken}
                  onChange={(e) => setActivationToken(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.activationToken
                      ? "border-red-500"
                      : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="Enter activation token"
                />
                {errors.activationToken && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.activationToken}
                  </p>
                )}
              </div>

              <TurnstileComponent onChange={handleVerifyTurnstileSubmit} />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-70 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Activating account...
                  </>
                ) : (
                  "Activate Account"
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setView("login")}
                className="text-purple-600 hover:text-purple-800 font-medium transition-colors"
              >
                Back to log in
              </button>
            </div>
          </>
        );

      case "forgotPassword":
        return (
          <>
            <h2 className="text-2xl font-semibold mb-6">Reset your password</h2>
            <p className="text-gray-600 mb-6">
              Enter your email address and we'll send you a link to reset your
              password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              <TurnstileComponent onChange={handleVerifyTurnstileSubmit} />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-70 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending reset link...
                  </>
                ) : (
                  "Send reset link"
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setView("login")}
                className="text-purple-600 hover:text-purple-800 font-medium transition-colors"
              >
                Back to log in
              </button>
            </div>
          </>
        );

      case "passwordResetSent":
        return (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold mb-4">Check your email</h2>
            <p className="text-gray-600 mb-6">
              We've sent a password reset link to {email}. Please check your
              inbox and follow the instructions.
            </p>
            <button
              type="button"
              onClick={() => setView("login")}
              className="text-purple-600 hover:text-purple-800 font-medium transition-colors"
            >
              Back to log in
            </button>
          </div>
        );

      case "resetPassword":
        return (
          <>
            <h2 className="text-2xl font-semibold mb-6">Set new password</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="Create a new password (min. 8 characters)"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.password}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reset Token
                </label>
                <input
                  type="text"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="The reset token sent to your email"
                />
              </div>

              <TurnstileComponent onChange={handleVerifyTurnstileSubmit} />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-70 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating password...
                  </>
                ) : (
                  "Set new password"
                )}
              </button>
            </form>
          </>
        );

      default:
        return null;
    }
  };

  return renderView();
};
export default AuthForm;
